import { Component, type ErrorInfo, type ReactNode } from 'react';

const SUPPORT_EMAIL = import.meta.env.VITE_BRAND_CONTACT_EMAIL ?? 'contact@voxfaunix.com';

interface State {
  failed: boolean;
}

/**
 * The last line of defence: a render error anywhere in the app lands here
 * instead of leaving a blank page.
 *
 * Deliberately self-contained — no router, no API, no shared components that
 * could themselves be what broke. It links to a plain `mailto:` rather than the
 * Contact page for the same reason: that page lives inside the app that just
 * failed.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[voxfauna] render error:', error, info.componentStack);
  }

  override render() {
    if (!this.state.failed) return this.props.children;

    return (
      <main className="main" role="alert">
        <p className="eyebrow">Something went wrong</p>
        <h1 className="page-title">That didn't work</h1>
        <p className="page-intro">
          The app hit an unexpected problem. Reloading usually sorts it out. Anything you had
          already submitted is safe, though a recording you hadn't sent yet may need to be made
          again.
        </p>
        <div className="stack" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
          <a
            className="btn btn--ghost"
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Something went wrong in the app')}`}
          >
            Tell us about it
          </a>
        </div>
      </main>
    );
  }
}
